import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, ShieldCheck } from "lucide-react";

const GENERIC_OK =
  "Şifre sıfırlama bağlantısı e-posta adresinize gönderildi. E-postayı gelen kutunuzda göremiyorsanız, lütfen Spam / Gereksiz Posta klasörünüzü de kontrol edin.";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Yanıt her koşulda aynı — hesap var/yok bilgisi açığa çıkarılmaz.
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
    } catch {
      // Sabit kalınır; kullanıcıya hâlâ genel mesaj gösterilir.
    } finally {
      setLoading(false);
      setDone(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative flex items-center gap-3">
          <img src="/penz-icon.png" alt="PENZ"
               className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 p-1 object-contain" />
          <img src="/penz-logo.png" alt="PENZ"
               className="h-6 w-auto object-contain brightness-0 invert" />
        </div>
        <div className="relative max-w-md">
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">Güvenli Sıfırlama</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight leading-tight">
            Şifrenizi <span className="text-emerald-400">güvenle</span> yenileyin.
          </h1>
          <p className="text-white/60 mt-4 text-sm leading-relaxed">
            E-posta adresinizi girin; kayıtlı hesabınız varsa 30 dakika geçerli
            bir bağlantı e-posta kutunuza düşer.
          </p>
          <div className="mt-8 flex items-center gap-2 text-xs text-white/60">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Bağlantı yalnızca bir kez kullanılabilir</span>
          </div>
        </div>
        <div className="relative text-xs text-white/40">© {new Date().getFullYear()} PENZ</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <img src="/penz-icon.png" alt="PENZ" className="w-9 h-9 rounded-lg object-contain" />
            <img src="/penz-logo.png" alt="PENZ" className="h-5 w-auto object-contain" />
          </div>

          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Şifremi Unuttum</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 mt-1">
            Şifre Sıfırlama Bağlantısı
          </h2>
          <p className="text-sm text-slate-500 mt-1.5">
            Hesabınıza kayıtlı e-posta adresini girin.
          </p>

          {done ? (
            <div
              className="mt-8 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700"
              data-testid="forgot-password-generic-message"
            >
              {GENERIC_OK}
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="forgot-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="fp-email" className="text-xs font-medium text-slate-700">E-posta</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="fp-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-11 rounded-lg border-slate-200 focus-visible:ring-slate-900/15 focus-visible:border-slate-900"
                    placeholder="ornek@firma.com"
                    required
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium"
                data-testid="forgot-password-submit-button"
              >
                {loading ? "Gönderiliyor..." : "Şifre Sıfırlama Bağlantısı Gönder"}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900"
              data-testid="forgot-password-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" /> Giriş ekranına dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
